// User
export const ADMIN_ROLE_NAME = "admin";

// Email validation (RFC 5321)
export const EMAIL_MIN_LENGTH = 5;
export const EMAIL_MAX_LENGTH = 254;
export const DEFAULT_ADMIN_USERNAME = "admin@nuclearn.ai";
export const DEFAULT_DEV_ADMIN_USERNAME = "devadmin@nuclearn.ai";

// Toolkit display name for the time series toolkit (matches PlatformToolKits.TIME_SERIES.value)
export const TIME_SERIES_TOOLKIT_NAME = "Time Series";

// Admin-only toolkits that should be hidden from non-admin users in persona creation
export const ADMIN_ONLY_TOOLKITS = [
  "Memory",
  "Agentic",
  "Project Genius",
  "Fastdraft",
  "Task",
  "Multiprompt",
  "External Actions",
  "Request for Additional Information",
];

// AtomAssist constants
export const GOOD_OR_NOT_GOOD_FEEDBACK_TYPE = 2;
export const DPO_FEEDBACK_TYPE = 3;

// File Upload Constants
export const MAX_FILES_PER_PROJECT = 20;

// Chunked Upload Constants
// Read from config.js, default to 50MB if not configured
export const CHUNKED_UPLOAD_THRESHOLD =
  (window.ENV?.CHUNKED_UPLOAD_THRESHOLD_MB || 50) * 1024 * 1024;

// File Accept Types - Extension to MIME type mapping
export const DOCUMENT_MIME_MAP: { [key: string]: string } = {
  // Text documents
  ".txt": "text/plain",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".pdf": "application/pdf",
  ".rtf": "application/rtf",
  ".odt": "application/vnd.oasis.opendocument.text",
  // Web documents
  ".html": "text/html",
  ".htm": "text/html",
  ".xhtml": "application/xhtml+xml",
  ".xml": "application/xml",
  ".md": "text/markdown",
  // Images
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".bmp": "image/bmp",
  ".tiff": "image/tiff",
  ".tif": "image/tiff",
  ".webp": "image/webp",
  ".svg": "image/svg+xml",
  // Presentations
  ".ppt": "application/vnd.ms-powerpoint",
  ".pptx":
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".odp": "application/vnd.oasis.opendocument.presentation",
  // Spreadsheets / Data files
  ".csv": "text/csv",
  ".tsv": "text/tsv",
  ".xls": "application/vnd.ms-excel",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xlsm": "application/vnd.ms-excel.sheet.macroEnabled.12",
  ".ods": "application/vnd.oasis.opendocument.spreadsheet",
  ".json": "application/json",
  // Other
  ".epub": "application/epub+zip",
  ".xer": "application/octet-stream",
};

const DOCUMENT_MIME_TYPES = [
  ...Object.keys(DOCUMENT_MIME_MAP),
  ...Object.values(DOCUMENT_MIME_MAP),
].join(", ");

// Document types the chat and project upload paths send for ingestion.
const CHAT_PROJECT_EXTENSIONS = [
  // Document files
  ".pdf",
  ".docx",
  ".doc",
  ".txt",
  ".rtf",
  ".odt",
  ".html",
  ".htm",
  ".xhtml",
  ".xml",
  ".md",
  // Data files
  ".csv",
  ".json",
  ".xlsx",
  ".xls",
  ".ods",
  ".xlsm",
  ".tsv",
  // Project schedule files
  ".xer",
  // Other common document formats
  ".ppt",
  ".pptx",
  ".odp",
  ".epub",
];

// Mirrors the backend dataset upload gate, Ingestion.supported_types(scope="dataset"):
// Gotenberg-convertible documents plus .xer schedules.
const DATASET_EXTENSIONS = [
  // Document files
  ".pdf",
  ".docx",
  ".doc",
  ".txt",
  ".rtf",
  ".odt",
  ".html",
  ".htm",
  ".xhtml",
  ".xml",
  ".epub",
  // Markup
  ".md",
  ".markdown",
  ".mdown",
  ".mkdn",
  ".rst",
  ".adoc",
  ".textile",
  ".wiki",
  // Data files
  ".csv",
  ".tsv",
  ".json",
  ".yaml",
  ".yml",
  ".xls",
  ".xlsx",
  ".xlsm",
  ".ods",
  // Presentations
  ".ppt",
  ".pptx",
  ".odp",
  // Vector graphics
  ".emf",
  ".wmf",
  // Project schedule files
  ".xer",
];

// Types only a backend native extractor claims; project folders accept them,
// datasets do not.
const FOLDER_TEXT_EXTENSIONS = [
  ".text",
  ".log",
  ".xlsb",
  // Config
  ".cfg",
  ".conf",
  ".ini",
  ".properties",
  ".toml",
  // Code
  ".py",
  ".pyw",
  ".js",
  ".jsx",
  ".ts",
  ".tsx",
  ".css",
  ".scss",
  ".sass",
  ".less",
  ".sql",
  ".sh",
  ".bash",
  ".zsh",
  ".fish",
  ".bat",
  ".cmd",
  ".ps1",
  ".psm1",
  ".c",
  ".cc",
  ".cpp",
  ".cxx",
  ".h",
  ".hpp",
  ".cs",
  ".java",
  ".kt",
  ".kts",
  ".scala",
  ".go",
  ".rs",
  ".rb",
  ".php",
  ".swift",
  ".m",
  ".mm",
  ".r",
];

// Mirrors the backend IMAGE_FILE_TYPES set: project folders store these as-is
// without document processing.
const IMAGE_EXTENSIONS = [
  ".png",
  ".svg",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".bmp",
  ".tif",
  ".tiff",
  ".ico",
  ".heic",
  ".heif",
];

export const ACCEPTED_FILE_TYPES = {
  // File extensions only (for simpler cases)
  EXTENSIONS: {
    CSV: [
      ".csv",
      "application/vnd.ms-excel",
      "text/csv",
      ".xlsx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ".ods",
      ".xlsm",
    ],
    PROJECT_XER: [".xer"],
    // Combined common document types for chat/project uploads
    CHAT_PROJECT: CHAT_PROJECT_EXTENSIONS,
    // Dataset uploads, matching the backend dataset gate.
    DATASET: DATASET_EXTENSIONS,
    // Project-folder uploads, matching the backend folder gate.
    PROJECT_FOLDER: [
      ...DATASET_EXTENSIONS,
      ...FOLDER_TEXT_EXTENSIONS,
      ...IMAGE_EXTENSIONS,
    ],
  },

  // Plain-language summaries of the lists above, for rejection messages.
  SUMMARIES: {
    DATASET: "documents, spreadsheets, and presentations",
    PROJECT_FOLDER:
      "documents, spreadsheets, presentations, images, and text or code files",
  },

  // MIME types with extensions (for dropzone compatibility)
  MIME_TYPES: {
    CSV: ".csv, application/vnd.ms-excel, text/csv, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .ods, application/vnd.oasis.opendocument.spreadsheet, .xlsm, application/vnd.ms-excel.sheet.macroEnabled.12",
    DOCUMENT: DOCUMENT_MIME_TYPES,
    PROJECT_XER: ".xer",
  },
};

// Observation Program
export const CRITERIA_DATASET_NAME = "OP_Criteria";
